import { CommonModule } from "@angular/common";
import { Component, computed, effect, forwardRef, input, output, signal, untracked } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";

import { AdaaHelper } from "../../../../core/utils";
import { Language } from "../../../constants/enums";
import { BlockedCharsDirective, PhoneNumberDirective } from "../../../directives";
import type { ParameterCatalog } from "../../../models";

const POSITIVE_INTEGER_BLOCKED_CHARS = ["-", "+", "e", "E", ".", ","];

@Component({
  selector: "adaa-form-input",
  imports: [CommonModule, ReactiveFormsModule, PhoneNumberDirective, BlockedCharsDirective],
  templateUrl: "./form-input.html",
  styleUrl: "./form-input.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormInputComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormInputComponent),
    },
  ],
})
export class FormInputComponent implements ControlValueAccessor {
  readonly AdaaHelper = AdaaHelper;
  readonly positiveIntegerBlockedChars = POSITIVE_INTEGER_BLOCKED_CHARS;
  readonly inputControl = new FormControl();

  private readonly _requiredFromValidator = signal<boolean>(false);
  private readonly _imperativeInvalid = signal<boolean>(false);
  private _disabledByInput = false;
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: string) => void;

  readonly showAsRequired = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly placeholder = input<string>("");
  readonly inputType = input<"text" | "number" | "positiveNumber" | "positiveInteger" | "tel" | "email" | "password">(
    "text"
  );
  readonly isTextArea = input<boolean>(false);
  readonly maxLength = input<number | null>(null);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly rows = input<number>(5);
  readonly classes = input<string>("form-control shadow-sm");
  readonly textAlignment = input<"default" | "left" | "right" | "center">(
    AdaaHelper.getCurrentLang() === Language.Arabic ? "right" : "left"
  );
  readonly direction = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly setDefaultValue = input<unknown>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isPositiveInteger = computed(() => this.inputType() === "positiveInteger");
  readonly controlType = computed(() =>
    this.inputType() === "positiveNumber" || this.isPositiveInteger() ? "number" : this.inputType()
  );
  readonly isRequired = computed(
    () => this.required() || this._requiredFromValidator() || this.inputControl.hasValidator(Validators.required)
  );
  readonly isInvalid = computed(() => this.invalid() || this._imperativeInvalid());

  constructor() {
    effect(() => {
      if (this.isDisabled()) {
        this._disabledByInput = true;
        this.inputControl.disable();
      } else if (this._disabledByInput) {
        this._disabledByInput = false;
        this.inputControl.enable();
      }
    });

    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      if (value) {
        untracked(() => this.writeValue(value as string));
      } else {
        this.inputControl.reset();
      }
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          this._requiredFromValidator.set(field.mandatory === "Y");
          this.inputControl.updateValueAndValidity();
        }
      }
    });

    // Re-run validation when inputType changes
    effect(() => {
      this.inputType();
      untracked(() => this._updateValidation());
    });
  }

  public writeValue(obj: string | number): void {
    if (obj === null) {
      this.inputControl.reset();
    }

    if (untracked(() => this.inputType()) === "positiveNumber" && typeof obj === "number") {
      obj = isNaN(Number(obj)) ? 0 : Math.abs(+obj);
    }

    this.inputControl.setValue(obj);
  }

  public registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(
    _value: FormControl
  ): false | { required?: boolean; whitespace?: boolean; notPositiveInteger?: boolean } {
    const required = this.isRequired();
    const isRequired = (required && this.inputControl.value === null) || (required && this.inputControl.value === "");

    if (isRequired) {
      return { required: true };
    }

    if (
      typeof this.inputControl.value === "string" &&
      this.inputControl.value.length > 0 &&
      this.inputControl.value.trim().length === 0
    ) {
      return { whitespace: true };
    }

    if (
      this.isPositiveInteger() &&
      !this._isEmpty(this.inputControl.value) &&
      !/^[1-9]\d*$/.test(`${this.inputControl.value}`)
    ) {
      return { notPositiveInteger: true };
    }

    return false;
  }

  public markInvalid(value: boolean): void {
    this._imperativeInvalid.set(value);
  }

  public valueChanged(event: unknown): void {
    if (this._onChange) this._onChange(event as string);
    this.inputChanges.emit(event);
  }

  private _updateValidation(): void {
    const validations = [];

    switch (this.inputType()) {
      case "number":
      case "positiveNumber":
        validations.push(Validators.pattern("^-?[0-9]\\d*(\\.\\d{1,2})?$"));
        break;
      case "positiveInteger":
        validations.push(Validators.pattern("^[1-9]\\d*$"));
        break;
      case "email":
        validations.push(Validators.email);
        break;
      default:
        break;
    }

    this.inputControl.setValidators([...validations]);
    this.inputControl.updateValueAndValidity();
  }

  private _isEmpty(value: unknown): boolean {
    return value === null || value === undefined || value === "";
  }
}
